import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppError, toUserMessage } from "../../lib/errors";
import { ReportPlanState } from "../../lib/report";
import { reportClient } from "../../lib/reportClient";

// backendが今後自然に進みうるstatusのみpollingを続ける
// (Backend App\Constants\ReportConstant::TERMINAL_STATUSESの補集合)。
// planning: agentが計画を生成中で自動的にplan_readyへ進む。
// queued/running: 実行中で自動的にterminal statusへ進む。
// plan_ready: startRun/updatePlanなどuser操作でのみ次へ進むためpollingしない。
// succeeded/partial/failed/cancelled: backend側のterminal statusで以後変化しない。
const ACTIVE_STATUSES = new Set(["planning", "queued", "running"]);
const INITIAL_POLL_INTERVAL_MS = 3_000;
const SLOW_POLL_INTERVAL_MS = 5_000;
const HIDDEN_POLL_INTERVAL_MS = 30_000;
const SLOW_POLL_AFTER_MS = 2 * 60_000;

export function getReportPollIntervalMs(elapsedMs: number, isHidden: boolean): number {
  if (isHidden) return HIDDEN_POLL_INTERVAL_MS;
  return elapsedMs >= SLOW_POLL_AFTER_MS ? SLOW_POLL_INTERVAL_MS : INITIAL_POLL_INTERVAL_MS;
}

// RequestCompanyResearchJobDetailPage.tsxのpolling実装を参考に、
// 一時的なサーバーエラーだけを区別してバックオフ再試行する。
const TRANSIENT_POLL_RETRY_DELAYS_MS = [2_000, 5_000, 10_000, 20_000, 30_000] as const;
const TRANSIENT_POLLING_MESSAGE = "一時的にサーバーへ接続できません。自動で再確認しています。";

function isTransientPollingError(error: unknown): boolean {
  if (!(error instanceof AppError)) return false;
  if (error.code === "NETWORK" || error.code === "TIMEOUT") return true;
  return error.meta.status === 502 || error.meta.status === 503 || error.meta.status === 504;
}

function getTransientPollDelayMs(consecutiveFailures: number): number {
  const index = Math.min(Math.max(consecutiveFailures - 1, 0), TRANSIENT_POLL_RETRY_DELAYS_MS.length - 1);
  return TRANSIENT_POLL_RETRY_DELAYS_MS[index];
}

export function useReportPolling(runId: string | undefined) {
  const navigate = useNavigate();
  const [run, setRun] = useState<ReportPlanState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transientMessage, setTransientMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!runId) { setLoading(false); return; }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let consecutiveTransientFailures = 0;
    const startedAt = Date.now();

    const clearTimer = () => {
      if (timer) {
        clearTimeout(timer);
        timer = undefined;
      }
    };

    const load = async () => {
      try {
        const next = await reportClient.getRun(runId);
        if (cancelled) return;
        consecutiveTransientFailures = 0;
        setRun(next);
        setError(null);
        setTransientMessage(null);
        if (ACTIVE_STATUSES.has(next.status)) {
          const isHidden = typeof document !== "undefined" && document.visibilityState === "hidden";
          timer = setTimeout(load, getReportPollIntervalMs(Date.now() - startedAt, isHidden));
        }
      } catch (reason) {
        if (cancelled) return;
        if (reason instanceof AppError && reason.code === "SESSION_EXPIRED") {
          navigate("/splash", { replace: true });
          return;
        }
        if (isTransientPollingError(reason)) {
          // 一時的なエラー: 直近取得できたrunの表示は残したまま、バックオフしつつ再試行を続ける。
          consecutiveTransientFailures += 1;
          setTransientMessage(TRANSIENT_POLLING_MESSAGE);
          timer = setTimeout(load, getTransientPollDelayMs(consecutiveTransientFailures));
        } else {
          // 非transientエラー(404/権限エラーなど): 打ち切ってユーザーへエラー表示する。
          // 直近取得できたrunはnullにしない(表示していた内容を消さない)。
          setError(toUserMessage(reason));
          setTransientMessage(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    // tab非表示中は間隔を延ばす一方、復帰時は延ばした分を待たず即時取得する。
    const handleVisibilityChange = () => {
      if (typeof document === "undefined" || document.visibilityState !== "visible") return;
      clearTimer();
      void load();
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }

    void load();
    return () => {
      cancelled = true;
      clearTimer();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
    };
  }, [runId, navigate]);

  return { run, loading, error, transientMessage };
}
